import React from 'react'
import { CustomCellRendererProps } from 'ag-grid-react'
import { ValidationIssue } from 'api'
import { RowDataGetter } from 'view/shared/datagrid'
import { isNil } from 'lodash'
import { getErrorMessages, getIssuesForField, maxSeverity, Severity } from 'model'
import { getTooltipContent, Tooltip } from 'view/components/tooltip'
import styled from '@emotion/styled'

export interface ValidationCellProps<T> extends CustomCellRendererProps<T> {
  className?: string
  dataTestId?: string
  getValidationIssues?: (params: ValidationCellProps<T>) => ValidationIssue[]
  informativeTooltip?: string
  /** option to center items via flexbox, useful when passing custom react children. */
  centered?: boolean
  /** option to create your own tooltip with a different formating while there are
   * no validation warnings, if you don't need custom formatting you can use
   * `informativeTooltip` instead. */
  getTooltipWhileNoValidations?: (params: ValidationCellProps<T>) => React.JSX.Element
  rowDataGetter?: RowDataGetter<any, any>
  delay?: number
  style?: React.CSSProperties
  validationField?: string | ((rowData: any) => string | null)
}

const ChildrenBox = styled.div<{ centered?: boolean }>`
  ${props =>
    props.centered &&
    `
  display: flex;
  align-items: center;
  margin: 0px;
  padding: 0px;

  `}
`

export const Validation = <T,>(props: React.PropsWithChildren<ValidationCellProps<T>>) => {
  return (
    <Tooltip delay={props.delay} tooltip={getTooltipInfo(props)}>
      <ChildrenBox
        data-test-id={props.dataTestId}
        centered={props.centered}
        className={`validation-cell ${getSeverity(props)} ${props.className ?? ''}`}
        style={props.style}>
        {props.children}
      </ChildrenBox>
    </Tooltip>
  )
}

export const ValidationCell = <T,>(props: ValidationCellProps<T>) => {
  return <Validation {...props}>{props.valueFormatted ?? props.value ?? ''}</Validation>
}

export const withValidation = <TData, TProps extends ValidationCellProps<TData>>(
  Component: React.ComponentType<TProps>
): React.ComponentType<TProps> => {
  return function WrappedComponentWithValidation(props: TProps) {
    return (
      <Validation
        getValidationIssues={props.getValidationIssues}
        rowDataGetter={props.rowDataGetter}
        validationField={props.validationField}
        {...props}>
        <Component {...props} />
      </Validation>
    )
  }
}

const getSeverity = <T,>(props: ValidationCellProps<T>) => {
  const validationIssues = getFieldValidationIssues(props)
  const severity = maxSeverity(validationIssues)

  if (severity === Severity.ERROR) return 'severity-error'
  else if (severity === Severity.WARN) return 'severity-warn'
  else if (severity === Severity.INFO) return 'severity-info'
  return ''
}

const getTooltipInfo = <T,>(props: ValidationCellProps<T>) => {
  const validationIssues = getFieldValidationIssues(props)
  let messages = getErrorMessages(validationIssues)

  if (props.informativeTooltip) {
    messages.push(props.informativeTooltip)
  }

  if (messages.length > 0) {
    return getTooltipContent(messages)
  }

  if (props.getTooltipWhileNoValidations) {
    return props.getTooltipWhileNoValidations(props)
  }

  return ''
}

const getFieldValidationIssues = <T,>(params: ValidationCellProps<T>) => {
  return getServerFieldValidationIssues(params).concat(getLocalValidationIssues(params))
}

const getServerFieldValidationIssues = <T,>(params: ValidationCellProps<T>): ValidationIssue[] => {
  if (isNil(params.validationField)) {
    return []
  }

  const rowData = params.rowDataGetter ? params.rowDataGetter(params, true) : params.node.data
  if (isNil(rowData)) {
    return []
  }

  const validationField = typeof params.validationField === 'string' ? params.validationField : params.validationField(rowData)
  if (isNil(validationField)) {
    return []
  }

  return getIssuesForField(rowData?.validationIssues || [], validationField)
}

const getLocalValidationIssues = <T,>(params: ValidationCellProps<T>): ValidationIssue[] => {
  if (isNil(params.getValidationIssues)) {
    return []
  }
  return params.getValidationIssues(params)
}
